import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";

export default function FinalCTA({ onJoin }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="final-cta-section" className="relative min-h-[92svh] flex items-center overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -top-[15%] -bottom-[15%]">
        <img
          src={IMAGES.finalCta}
          alt="Athlete mid-training in dramatic light"
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-ink/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />

      <div className="relative z-10 px-5 sm:px-10 lg:px-16 py-28 w-full">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-volt mb-6"
        >
          Your move
        </motion.p>
        <h2 className="font-display uppercase leading-[0.85] text-[16vw] sm:text-[11vw] lg:text-[9vw]">
          <span className="block overflow-hidden">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              Ready to become
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              className="block text-volt"
              initial={{ y: "110%" }}
              whileInView={{ y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              more capable.
            </motion.span>
          </span>
        </h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-6 text-zinc-300 text-base sm:text-lg max-w-md"
        >
          Train your body for everything life demands from it.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href="/assessment"
            data-testid="final-cta-start-button"
            className="group inline-flex items-center gap-3 bg-volt text-black uppercase tracking-[0.18em] text-xs sm:text-sm font-extrabold px-8 py-4 hover:bg-white transition-colors duration-300"
          >
            Take the Assessment
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </a>
          <a
            href={`https://wa.me/919811783739?text=${encodeURIComponent("Hi Capable 360, I would like to know more about your training programs.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-whatsapp-button"
            className="inline-flex items-center gap-3 border border-zinc-400 text-white uppercase tracking-[0.18em] text-xs sm:text-sm font-bold px-8 py-4 hover:border-volt hover:text-volt transition-colors duration-300"
          >
            Chat on WhatsApp
          </a>
        </motion.div>
      </div>
    </section>
  );
}
